import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import SEO from '../../components/SEO';

const PageContainer = styled.div`
  padding: 3rem 0;
`;

const FilterContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const FilterButton = styled.button`
  background-color: ${props => props.active ? 'var(--primary)' : 'white'};
  color: ${props => props.active ? 'white' : 'var(--dark)'};
  border: 1px solid ${props => props.active ? 'var(--primary)' : '#e0e0e0'};
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${props => props.active ? 'var(--primary-dark)' : '#f8f9fa'};
  }
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 2rem;
`;

const ProductCard = styled.div`
  background-color: white;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  }
`;

const ProductImage = styled.div`
  height: 200px;
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center;
`;

const ProductContent = styled.div`
  padding: 1.5rem;
`;

const ProductTitle = styled.h3`
  margin-bottom: 0.5rem;
  font-size: 1.2rem;
`;

const ProductPrice = styled.div`
  color: var(--primary);
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const ProductRating = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  
  .stars {
    color: #FFD700;
    margin-right: 0.5rem;
  }
  
  .count {
    color: var(--gray);
    font-size: 0.9rem;
  }
`;

const ProductDescription = styled.p`
  color: var(--gray);
  font-size: 0.9rem;
  margin-bottom: 1rem;
`;

const ProductLink = styled.a`
  display: inline-block;
  color: var(--primary);
  text-decoration: none;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

const AgeTag = styled.span`
  display: inline-block;
  background-color: var(--secondary-light);
  color: var(--dark);
  font-size: 0.8rem;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;
`;

const InfoSection = styled.div`
  background-color: #f8f9fa;
  border-radius: 16px;
  padding: 2rem;
  margin-top: 3rem;
  
  h3 {
    margin-bottom: 1rem;
    color: var(--dark);
  }
  
  p {
    color: var(--gray);
    margin-bottom: 1rem;
  }
  
  ul {
    padding-left: 1.5rem;
    margin-bottom: 1rem;
    
    li {
      margin-bottom: 0.5rem;
    }
  }
`;

// Mock product data
const products = [
  {
    id: 1,
    name: "Halo SleepSack Swaddle",
    price: "$32.99",
    rating: 4.8,
    reviews: 1245,
    description: "Adjustable swaddle that allows for arms-in or arms-out swaddling for safe and comfortable sleep.",
    image: "https://m.media-amazon.com/images/I/71ePXM9WkPL._AC_SL1500_.jpg",
    category: "swaddles",
    ageRange: ["0-3 months", "3-6 months"]
  },
  {
    id: 2,
    name: "White Noise Machine",
    price: "$39.99",
    rating: 4.6,
    reviews: 987,
    description: "Portable sound machine with 20 non-looping sounds to help your baby fall asleep faster and stay asleep longer.",
    image: "https://m.media-amazon.com/images/I/61bwANOIiDL._AC_SL1500_.jpg",
    category: "sound-machines",
    ageRange: ["0-3 months", "3-6 months", "6-12 months", "1-2 years", "2+ years"]
  },
  {
    id: 3,
    name: "Blackout Curtains",
    price: "$45.99",
    rating: 4.7,
    reviews: 856,
    description: "100% blackout curtains to create the perfect dark sleep environment for naps and nighttime sleep.",
    image: "https://m.media-amazon.com/images/I/71Qnh7sXBPL._AC_SL1500_.jpg",
    category: "room-darkening",
    ageRange: ["0-3 months", "3-6 months", "6-12 months", "1-2 years", "2+ years"]
  },
  {
    id: 4,
    name: "Baby Sleep Sack",
    price: "$28.99",
    rating: 4.9,
    reviews: 1102,
    description: "Wearable blanket made from breathable cotton to keep your baby warm and safe during sleep.",
    image: "https://m.media-amazon.com/images/I/71Jb6hUhkAL._AC_SL1500_.jpg",
    category: "sleep-sacks",
    ageRange: ["3-6 months", "6-12 months", "1-2 years"]
  },
  {
    id: 5,
    name: "Baby Monitor",
    price: "$159.99",
    rating: 4.5,
    reviews: 2345,
    description: "Video baby monitor with night vision, temperature monitoring, and two-way audio communication.",
    image: "https://m.media-amazon.com/images/I/61FrxlR0RlL._AC_SL1500_.jpg",
    category: "monitors",
    ageRange: ["0-3 months", "3-6 months", "6-12 months", "1-2 years", "2+ years"]
  },
  {
    id: 6,
    name: "Crib Mattress",
    price: "$89.99",
    rating: 4.7,
    reviews: 789,
    description: "Dual-sided crib mattress with firm infant side and softer toddler side for extended use.",
    image: "https://m.media-amazon.com/images/I/71e9GQ3KnrL._AC_SL1500_.jpg",
    category: "bedding",
    ageRange: ["0-3 months", "3-6 months", "6-12 months", "1-2 years", "2+ years"]
  }
];

const StorePage = () => {
  const [activeCategory, setActiveCategory] = useState("all");
  const [activeAgeRange, setActiveAgeRange] = useState("all");
  
  // SEO metadata based on the SEO strategy document
  const seoData = {
    title: "Baby Sleep Products | Recommended Tools for Better Sleep | Dozzy",
    description: "Shop curated baby sleep products recommended by sleep experts. Find tools to complement your personalized Dozzy sleep plan.",
    keywords: [
      "baby sleep products", 
      "sleep training tools", 
      "baby sleep aids",
      "white noise machine",
      "blackout curtains",
      "sleep sacks",
      "baby monitors",
      "swaddles"
    ],
    structuredData: {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "itemListElement": products.map((product, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "Product",
          "name": product.name,
          "description": product.description,
          "image": product.image,
          "offers": {
            "@type": "Offer",
            "price": product.price.replace("$", ""),
            "priceCurrency": "AUD"
          },
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": product.rating,
            "reviewCount": product.reviews
          }
        }
      }))
    }
  };
  
  // Force scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    console.log('StorePage: Forced scroll to top on mount');
  }, []);
  
  const categories = [
    { id: "all", name: "All Products" },
    { id: "swaddles", name: "Swaddles" },
    { id: "sleep-sacks", name: "Sleep Sacks" },
    { id: "sound-machines", name: "Sound Machines" },
    { id: "monitors", name: "Baby Monitors" },
    { id: "room-darkening", name: "Room Darkening" },
    { id: "bedding", name: "Bedding" }
  ];
  
  const ageRanges = [
    { id: "all", name: "All Ages" },
    { id: "0-3 months", name: "0-3 Months" },
    { id: "3-6 months", name: "3-6 Months" },
    { id: "6-12 months", name: "6-12 Months" },
    { id: "1-2 years", name: "1-2 Years" },
    { id: "2+ years", name: "2+ Years" }
  ];
  
  const filteredProducts = products.filter(product => 
    (activeCategory === "all" || product.category === activeCategory) &&
    (activeAgeRange === "all" || product.ageRange.includes(activeAgeRange))
  );
  
  return (
    <>
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        structuredData={seoData.structuredData}
        canonicalUrl="https://dozzy.au/store"
        ogType="website"
      />
      
      {/* Page Header */}
      <section className="py-5 bg-primary text-white text-center">
        <div className="container py-2">
          <h1>Sleep Product Store</h1>
          <p className="mx-auto" style={{ maxWidth: '700px' }}>
            Curated products to help your little one sleep better, recommended by sleep experts
          </p>
        </div>
      </section>
      
      <div className="container">
        {/* Featured Gift Section */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <h2>The Perfect Gift for New Parents</h2>
              <p className="mb-4">Help someone you care about get the rest they deserve with a personalized AI sleep plan. Our gift option makes it easy to give the gift of sleep.</p>
              <div className="d-flex flex-wrap gap-3">
                <Link to="/gift" className="btn btn-red">
                  <i className="bi bi-gift me-2"></i> Gift a Sleep Plan
                </Link>
                <Link to="/faq" className="btn btn-outline-red">Learn More</Link>
              </div>
            </div>
            <div className="col-lg-6">
              <img src="/images/gift-sleep.jpg" alt="Gift of Sleep" className="img-fluid rounded-4 shadow" 
                   onError={(e) => {e.target.onerror = null; e.target.src = "/images/baby-7540912_1280.jpg"}} />
            </div>
          </div>
        </div>
      </section>

      <PageContainer>
          <div className="mb-4">
            <h2 className="mb-3">Categories</h2>
            <FilterContainer>
              {categories.map(category => (
                <FilterButton 
                  key={category.id}
                  active={activeCategory === category.id}
                  onClick={() => setActiveCategory(category.id)}
                >
                  {category.name}
                </FilterButton>
              ))}
            </FilterContainer>
          </div>
          
          <div className="mb-4">
            <h2 className="mb-3">Age Range</h2>
            <FilterContainer>
              {ageRanges.map(range => (
                <FilterButton 
                  key={range.id}
                  active={activeAgeRange === range.id}
                  onClick={() => setActiveAgeRange(range.id)}
                >
                  {range.name}
                </FilterButton>
              ))}
            </FilterContainer>
          </div>
          
          {filteredProducts.length > 0 ? (
            <ProductGrid>
              {filteredProducts.map(product => (
                <ProductCard key={product.id}>
                  <ProductImage src={product.image} />
                  <ProductContent>
                    <ProductTitle>{product.name}</ProductTitle>
                    <ProductPrice>{product.price}</ProductPrice>
                    <ProductRating>
                      <div className="stars">
                        {'★'.repeat(Math.floor(product.rating))}
                        {product.rating % 1 !== 0 ? '½' : ''}
                        {'☆'.repeat(5 - Math.ceil(product.rating))}
                      </div>
                      <div className="count">({product.reviews})</div>
                    </ProductRating>
                    <div className="mb-3">
                      {product.ageRange.map(age => (
                        <AgeTag key={age}>{age}</AgeTag>
                      ))}
                    </div>
                    <ProductDescription>{product.description}</ProductDescription>
                    <ProductLink href="#" target="_blank" rel="noopener noreferrer">
                      View on Amazon
                    </ProductLink>
                  </ProductContent>
                </ProductCard>
              ))}
            </ProductGrid>
          ) : (
            <div className="text-center py-5">
              <p className="mb-3">No products found matching your filters.</p>
              <button 
                className="btn btn-outline-primary"
                onClick={() => {
                  setActiveCategory("all");
                  setActiveAgeRange("all");
                }}
              >
                Clear filters
              </button>
            </div>
          )}
          
          <InfoSection>
            <h3>Creating the Optimal Sleep Environment</h3>
            <p>A good sleep environment can significantly improve your child's sleep quality. Here are some key elements to consider:</p>
            <ul>
              <li><strong>Temperature:</strong> Keep the room between 18-22°C (65-72°F) for optimal sleep.</li>
              <li><strong>Darkness:</strong> Use blackout curtains to create a dark environment that promotes melatonin production.</li>
              <li><strong>White Noise:</strong> A consistent background noise can help mask household sounds and soothe your baby.</li>
              <li><strong>Safe Sleep Surface:</strong> Use a firm mattress with a fitted sheet in a safety-approved crib or bassinet.</li>
              <li><strong>Sleep Clothing:</strong> Use appropriate swaddles or sleep sacks instead of loose blankets for safety.</li>
            </ul>
            <p>The products in our store have been carefully selected to help you create the ideal sleep environment for your child, following safe sleep guidelines from Red Nose Australia and the American Academy of Pediatrics.</p>
          </InfoSection>
          
          <div className="mt-4 text-center">
            <p className="text-muted small">
              Disclosure: As an Amazon Associate, we earn from qualifying purchases. This means that if you click on a product link and make a purchase, we may receive a small commission at no extra cost to you.
            </p>
          </div>
        </PageContainer>
      </div>
    </>
  );
};

export default StorePage;
